/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState, useRef, useMemo } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'

import { Link } from '@ta-interaktiv/contentbricks'
import { MetaInfos } from 'components/MetaInfos'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { ClusterCannon } from 'components/clusterCannon'
import scrollama from 'scrollama'
import { NameComparission } from 'components/charts/NameComparission'
import { NameInCulture } from 'components/charts/NameInCulture'
import { Top10 } from 'components/charts/Top10'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import AnchorLink from 'react-anchor-link-smooth-scroll'

// TODO Set the correct article ID here
const ARTICLE_ID = '246112984283'

export function AppComponent() {
  const publicationDomain = parseHostname().publicationDomain
  const firstStageRef = useRef()
  const scroller = useRef(scrollama())
  const [dataForComp, setDatAForComp] = useState()
  const [newborn, setNewborn] = useState()
  const [stage, setStage] = useState({
    id: 0,
    boundingRect: {
      top: 0,
      height: 0
    }
  })

  useEffect(() => {
    const urls = [
      require('../_data/chart_data.csv'),
      require('../_data/population.csv'),
      require('../_data/womens_2021.csv'),
      require('../_data/mens_2021.csv'),
      require('../_data/swiss_names.csv'),
      require('../_data/swiss_names_2021.csv')
    ]

    setDatAForComp([urls[1], urls[2], urls[3]])

    const swissNames2019 = urls[5]
    const swissBoys = swissNames2019
      .filter(f => f.gender === 'm')
      .sort((a, b) => +b.count - +a.count)
      .map((m, i) => {
        return [m.name, +m.count, i + 1]
      })
    const swissGirls = swissNames2019
      .filter(f => f.gender === 'f')
      .sort((a, b) => +b.count - +a.count)
      .map((m, i) => {
        return [m.name, +m.count, i + 1]
      })
    setNewborn([swissBoys, swissGirls])
  }, [])

  const handleResize = () => {
    scroller.current.resize()
  }

  useEffect(() => {
    scroller.current
      .setup({
        step: '.step',
        progress: true,
        offset: 1
      })
      .onStepEnter(response => {
        setStage({
          id: +response.element.dataset.step,
          boundingRect: {
            top: response.element.getBoundingClientRect().top + window.scrollY,
            height: response.element.getBoundingClientRect().height
          }
        })
      })
      .onStepProgress(response => {
        if (response.progress >= 1) {
          setStage({
            id: +response.element.dataset.step + 0.5,
            boundingRect: {
              top:
                response.element.getBoundingClientRect().top + window.scrollY,
              height: response.element.getBoundingClientRect().height
            }
          })
        }
      })

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const bcr = firstStageRef.current.getBoundingClientRect()
    setStage({
      id: 0,
      boundingRect: {
        top: bcr.top,
        height: bcr.height
      }
    })
  }, [firstStageRef])

  return (
    <div className='index'>
      <Masthead inverted articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <ClusterCannon stage={stage} />

      <header>
        <div className='ui inverted container'>
          <div className='ui header'>
            <div className='top-line'>Wie heissen unsere Babys?</div>
            <h1>Das sind die beliebtesten Vornamen der Schweiz</h1>
            <h2>
              Welches waren die häufigsten Vornamen des letzten Jahres in der
              Schweiz? Und wie haben sich diese Namen über Jahrzehnte hinweg
              entwickelt? Entdecken Sie alle Zahlen in unserem interaktiven
              Tool.
            </h2>
          </div>
        </div>
      </header>

      <div className='step ghost one' data-step={1} ref={firstStageRef} />

      <div className='ui vertical very fitted segment'>
        <div className='ui text inverted container'>
          <MetaInfos
            authors={[
              'Fanny Giroud',
              'Mathieu Rudaz',
              'Sebastian Broschinski',
              'Dominik Balmer'
            ]}
            updated='Aktualisiert am 23. August 2022'
          />
          <div className='ta text'>
            <p>
              Wie viele kleine Mädchen oder kleine Buben werden denselben
              Vornamen haben wie mein Kind? Das ist die Frage, die sich
              vermutlich alle Eltern stellen, wenn sie die grosse Wahl treffen
              und einen Vornamen für ihr Kind bestimmen. Dieser Leitfaden führt
              alle werdenden Eltern in vier Kapiteln über den schmalen Grat
              zwischen Popularität und Originalität.
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <ol className='menu'>
              <li>
                <AnchorLink href='#chapter-1' className='nav-link'>
                  1. Die Vornamen der{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    Neugeborenen <i className='ui icon chevron down' />
                  </span>
                </AnchorLink>
              </li>
              <li>
                <a
                  href={`https://${publicationDomain}/132736369667`}
                  target='_blank'
                  rel='noreferrer'
                >
                  2. Warum die Vornamen der Babys immer ausgefallener{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    werden <span className='abo' />
                    <i className='ui icon external alternate' />
                  </span>
                </a>
              </li>
              <li>
                <AnchorLink href='#chapter-3' className='nav-link'>
                  3. So entwickelten sich die Vornamen der Schweizer{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    Bevölkerung <i className='ui icon chevron down' />
                  </span>
                </AnchorLink>
              </li>
              <li>
                <AnchorLink href='#chapter-4' className='nav-link'>
                  4. Wenn die Popkultur die Namenswahl{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    beeinflusst <i className='ui icon chevron down' />
                  </span>
                </AnchorLink>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment' id='chapter-1'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <h4>
              <div>
                <div className='number'>1</div>
              </div>
              <div>Die beliebtesten Vornamen der Neugeborenen im Jahr 2021</div>
              <div className='line' />
            </h4>
            <p>
              Im Jahr 2021 waren in der Schweiz die Vornamen Noah, Liam und
              Matteo für neugeborene Buben am beliebtesten. Bei den Mädchen
              führten Mia, Emma und Elena die Rangliste an. Das zeigt die neue
              Vornamen-Hitparade des Bundesamts für Statistik (BFS).
            </p>
            <p>
              Der Vorname Noah, der bereits 2010, 2011 sowie von 2013 bis 2017
              am beliebtesten war, behielt 2021 wieder die Spitzenposition und
              verdrängte Liam auf den zweiten Rang. Bei den Mädchen verteidigte
              Mia den ersten Platz, gefolgt von Emma. Mia war bereits 2013,
              2015, 2016 und 2019 am häufigsten vergeben worden.
            </p>
            <p>
              Die Vornamen Vera bei den Mädchen und Zayn bei den Knaben machten
              in einem Jahr die meisten Plätze in der Rangliste gut. Sie rückten
              um 131 beziehungsweise 242 Plätze nach oben. Die grösste
              Absteigerin im gleichen Zeitraum war Matilda, der grösste
              Absteiger Lionel; sie verloren 82 beziehungsweise 62 Plätze. Neu
              gehören Amira und Emilio zu den 100 beliebtesten Vornamen von
              Neugeborenen.
            </p>
          </div>
        </div>
      </div>

      {newborn && <Top10 data={newborn} />}

      <div className='step ghost one' data-step={2} />

      <div className='teaser ui text container '>
        <h4 style={{ marginBottom: 0 }}>
          <div className='number'>2</div>
        </h4>
        <div className='title'>
          Warum die Vornamen der Babys immer ausgefallener werden
        </div>
        <div className='lead'>
          Jihad, Georg III. oder J’Adore sind als Vornamen in der Schweiz
          erlaubt. Mit Batman, Schmucki und Jesus von Nazareth hingegen sind die
          Eltern bei den Behörden abgeblitzt.
        </div>
        <div className='line' />
        <div className='carrot'>
          <div className='ta text inverted'>
            <p>
              In der Schweiz dürfen Eltern ihren neugeborenen Kindern keinen
              x-beliebigen Vornamen geben. Doch wer entscheidet, ob ein
              bestimmter Vorname erlaubt ist oder nicht? Und wo liegt die Grenze
              dessen, was toleriert wird?
            </p>
            <p>
              Ein wichtiger Grundsatz, der in der schweizerischen
              Zivilstandsverordnung verankert ist, lautet: Der Vorname darf die
              Kindesinteressen nicht gefährden. Die Beurteilung obliegt den
              kantonalen Zivilstandsbeamtinnen und -beamten, die die Geburten in
              das offizielle Register eintragen.
            </p>
          </div>
        </div>

        <div className='ui primary button inverted'>
          <Link
            href='132736369667'
            onClick={() => {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `href:Ganzen Artikel lesen`
              })
            }}
          >
            Ganzen Artikel lesen →
          </Link>
        </div>
      </div>

      <div className='ui vertical very fitted segment' id='chapter-3'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <h4>
              <div>
                <div className='number'>3</div>
              </div>
              <div>
                So entwickelten sich die Vornamen der Schweizer Bevölkerung
              </div>
              <div className='line' />
            </h4>
            <p>
              Die Vornamensstatistik des Bundesamts für Statistik (BFS) zeigt,
              welche Vornamen in der Schweiz am häufigsten sind – erfasst sind
              darin nicht nur die Neugeborenen. Die untenstehende Grafik zeigt
              die Beliebtheit einzelner Vornamen im Laufe der Jahre.
            </p>
            <p>
              Die Visualisierung zeigt die Häufigkeit von Vornamen in der
              aktuellen Wohnbevölkerung der Schweiz für jedes Geburtsjahr. Da
              verstorbene Personen in den Daten nicht mehr berücksichtigt
              werden, ist die Zahl der Vornamen in den ersten Jahren geringer.
              Aus diesem Grund operiert die Grafik mit relativen Werten.
            </p>
            <p />
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui inverted container' style={{ margin: '1.5em 0' }}>
          {dataForComp && <NameComparission data={dataForComp} />}
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui text inverted container'>
          <div className='ui inverted small message'>
            <div className='ta text'>
              {/* <h4>Ce que montrent ces données</h4> */}
              <div className='item'>
                Berücksichtigt sind nur die 1000 häufigsten Vornamen je
                Geschlecht.
              </div>
              <div className='item'>
                Vornamen, die weniger als dreimal vorkommen, sind nicht erfasst.
              </div>
              <div className='item'>
                Bei Vornamen, die aus mehreren Wörtern bestehen, wird der
                gebräuchliche Vorname oder der erste Vorname berücksichtigt.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='step ghost one' data-step={3} />

      <div className='ui vertical very fitted segment' id='chapter-4'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <h4>
              <div>
                <div className='number'>4</div>
              </div>
              <div> Wenn die Popkultur die Namenswahl beeinflusst</div>
              <div className='line' />
            </h4>
            <p>
              Songs, Filme, Werbung, Politik, Sport? Welche Ereignisse
              beeinflussen die Eltern in der Schweiz und damit die Wahl eines
              Vornamens für ihre Kinder? Hier sehen Sie unsere Auswahl an
              Vornamen mit berühmten Trägerinnen und Trägern. Die Grafik zeigt,
              wie sich diese Namen über die Zeit entwickeln.
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui inverted container' style={{ margin: '1.5em 0' }}>
          {dataForComp && <NameInCulture data={dataForComp} />}
        </div>
      </div>

      <Footer
        locale='de'
        inverted
        articleId={ARTICLE_ID}
        hidePortfolioTeaser
        mailTo='interaktiv@tamedia.ch'
        credits={[
          ['Texte', 'Fanny Giroud, Dominik Balmer'],
          ['Grafiken', 'Sebastian Broschinski, Mathieu Rudaz'],
          ['Konzept und Umsetzung', 'Mathieu Rudaz, Mathias Lutz']
        ]}
      />
    </div>
  )
}
